import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const resolveUrl = (u) => (u.startsWith("/") ? `${API}${u}` : u);

export default function GalleryPage() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPhotos(data.map((p) => ({ src: resolveUrl(p.url), alt: p.caption || "Texoma Resorts" }))))
      .catch(() => setPhotos([]));
  }, []);

  return (
    <>
      <Helmet>
        <title>Gallery | Photos of Texoma Resorts Near Lake Texoma</title>
        <meta name="description" content="Browse photos of Texoma Resorts - luxury cabins, glamping tents, geodesic domes, camping sites, and Lake Texoma views at Serenity Village." />
      </Helmet>

      <section className="pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-accent mb-3">Visual Tour</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-foreground tracking-tight">
              Gallery
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground mt-4 max-w-xl">
              Explore the beauty of Serenity Village and Lake Texoma through our gallery.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="gallery-grid" className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {photos === null ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>
          ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {photos.map((img, i) => (
              <motion.div
                key={img.src}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                className="break-inside-avoid"
              >
                <div
                  className="img-hover-zoom rounded-lg overflow-hidden cursor-pointer"
                  onClick={() => setSelectedImage(img)}
                  data-testid={`gallery-image-${i}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            ))}
          </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 text-white/70 hover:text-white"
              data-testid="lightbox-close"
            >
              <X size={28} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
